import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, TrendingUp, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { subDays, isAfter, format, startOfDay } from "date-fns";
import { ar } from "date-fns/locale";

const SupportStats = () => {
  const { data: tickets } = useQuery({
    queryKey: ["support-stats-tickets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("id, status, priority, category, created_at, resolved_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const all = tickets || [];
  const last7Days = all.filter(t => isAfter(new Date(t.created_at), subDays(new Date(), 7)));
  const last30Days = all.filter(t => isAfter(new Date(t.created_at), subDays(new Date(), 30)));

  const resolvedTickets = all.filter(t => t.resolved_at);
  const avgResolutionMs = resolvedTickets.length > 0
    ? resolvedTickets.reduce((acc, t) => acc + (new Date(t.resolved_at!).getTime() - new Date(t.created_at).getTime()), 0) / resolvedTickets.length
    : 0;
  const avgResolutionHours = Math.round(avgResolutionMs / (1000 * 60 * 60));

  const categoryCount = all.reduce((acc: Record<string, number>, t) => {
    acc[t.category] = (acc[t.category] || 0) + 1;
    return acc;
  }, {});

  const priorityCount = all.reduce((acc: Record<string, number>, t) => {
    acc[t.priority] = (acc[t.priority] || 0) + 1;
    return acc;
  }, {});

  const statusCount = all.reduce((acc: Record<string, number>, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  const categoryLabels: Record<string, string> = {
    general: "عام", login: "تسجيل الدخول", upload: "رفع ملفات",
    live: "بث مباشر", billing: "فواتير", technical: "تقني", call: "مكالمة",
  };

  const priorityLabels: Record<string, string> = {
    low: "منخفضة", medium: "متوسطة", high: "عالية", urgent: "عاجلة",
  };

  const statusLabels: Record<string, string> = {
    open: "مفتوحة", in_progress: "قيد المعالجة", resolved: "تم الحل", closed: "مغلقة",
  };

  // Daily chart for last 7 days
  const dailyCounts: { day: string; count: number }[] = [];
  for (let i = 6; i >= 0; i--) {
    const day = subDays(new Date(), i);
    const dayStart = startOfDay(day);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    const count = all.filter(t => {
      const d = new Date(t.created_at);
      return d >= dayStart && d < dayEnd;
    }).length;
    dailyCounts.push({ day: format(day, "EEE", { locale: ar }), count });
  }
  const maxDaily = Math.max(...dailyCounts.map(d => d.count), 1);

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-primary" />
          إحصائيات الدعم الفني
        </h1>
        <p className="text-muted-foreground text-sm mt-1">تحليل أداء فريق الدعم</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{last7Days.length}</p>
            <p className="text-xs text-muted-foreground mt-1">تذاكر آخر 7 أيام</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{last30Days.length}</p>
            <p className="text-xs text-muted-foreground mt-1">تذاكر آخر 30 يوم</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{avgResolutionHours}h</p>
            <p className="text-xs text-muted-foreground mt-1">متوسط وقت الحل</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-destructive">{statusCount["open"] || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">تنتظر الرد</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Bar Chart (CSS-based) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            التذاكر اليومية (آخر 7 أيام)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 h-40">
            {dailyCounts.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs font-medium">{d.count}</span>
                <div
                  className="w-full bg-primary/80 rounded-t-md transition-all"
                  style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? "8px" : "2px" }}
                />
                <span className="text-[10px] text-muted-foreground">{d.day}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* By Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">حسب الحالة</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(statusCount).map(([key, count]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{statusLabels[key] || key}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        key === "open" ? "bg-blue-500" :
                        key === "in_progress" ? "bg-amber-500" :
                        key === "resolved" ? "bg-green-500" : "bg-muted-foreground"
                      }`}
                      style={{ width: `${(count / all.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8 text-left">{count}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* By Priority */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">حسب الأولوية</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {["urgent", "high", "medium", "low"].map((key) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{priorityLabels[key]}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        key === "urgent" ? "bg-destructive" :
                        key === "high" ? "bg-amber-500" :
                        key === "medium" ? "bg-blue-500" : "bg-muted-foreground"
                      }`}
                      style={{ width: `${((priorityCount[key] || 0) / Math.max(all.length, 1)) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8 text-left">{priorityCount[key] || 0}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* By Category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">حسب التصنيف</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(categoryCount)
              .sort((a, b) => b[1] - a[1])
              .map(([key, count]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{categoryLabels[key] || key}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${(count / Math.max(all.length, 1)) * 100}%` }} />
                  </div>
                  <span className="text-sm font-medium w-8 text-left">{count}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupportStats;
